import React from 'react';
import { AlertCircle, CheckCircle, Info, AlertTriangle, X } from 'lucide-react';

const AlertDialog = ({
  title,
  message,
  type = 'info', // success, error, warning, info
  buttonText = 'OK',
  onClose,
}) => {
  const types = {
    success: {
      icon: CheckCircle,
      iconColor: 'var(--success-600)',
      iconBg: 'var(--success-100)',
      btnClass: 'btn-success',
    },
    error: {
      icon: AlertCircle,
      iconColor: 'var(--danger-600)',
      iconBg: 'var(--danger-100)',
      btnClass: 'btn-danger',
    },
    warning: {
      icon: AlertTriangle,
      iconColor: 'var(--warning-600)',
      iconBg: 'var(--warning-100)',
      btnClass: 'btn-warning',
    },
    info: {
      icon: Info,
      iconColor: 'var(--primary-600)',
      iconBg: 'var(--primary-100)',
      btnClass: 'btn-primary',
    },
  };

  const config = types[type] || types.info;
  const Icon = config.icon;

  return (
    <div className="alert-dialog-overlay" onClick={onClose}>
      <div className="alert-dialog" onClick={(e) => e.stopPropagation()}>
        <button className="alert-dialog-close" onClick={onClose}>
          <X size={20} />
        </button>

        <div className="alert-dialog-icon" style={{ backgroundColor: config.iconBg, color: config.iconColor }}>
          <Icon size={32} />
        </div>

        <div className="alert-dialog-content">
          {title && <h3 className="alert-dialog-title">{title}</h3>}
          <p className="alert-dialog-message">{message}</p>
        </div>

        <div className="alert-dialog-actions">
          <button className={`btn ${config.btnClass}`} onClick={onClose} autoFocus>
            {buttonText}
          </button>
        </div>
      </div>

      <style jsx>{`
        .alert-dialog-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 1rem;
          animation: fadeIn 0.2s ease-out;
        }

        .alert-dialog {
          background: white;
          border-radius: var(--radius-xl);
          box-shadow: var(--shadow-2xl);
          max-width: 380px;
          width: 100%;
          padding: 2rem;
          position: relative;
          animation: slideUp 0.3s ease-out;
        }

        .alert-dialog-close {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: none;
          border: none;
          color: var(--gray-400);
          cursor: pointer;
          padding: 0.25rem;
          border-radius: var(--radius-sm);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all var(--transition-fast);
        }

        .alert-dialog-close:hover {
          background-color: var(--gray-100);
          color: var(--gray-600);
        }

        .alert-dialog-icon {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
        }

        .alert-dialog-content {
          text-align: center;
          margin-bottom: 2rem;
        }

        .alert-dialog-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--gray-900);
          margin: 0 0 0.5rem 0;
        }

        .alert-dialog-message {
          font-size: 0.875rem;
          color: var(--gray-600);
          margin: 0;
          line-height: 1.5;
        }

        .alert-dialog-actions {
          display: flex;
          justify-content: center;
        }

        .alert-dialog-actions .btn {
          min-width: 120px;
          padding: 0.75rem 2rem;
          font-weight: 500;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @media (max-width: 480px) {
          .alert-dialog {
            padding: 1.5rem;
          }

          .alert-dialog-actions .btn {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default AlertDialog;

